import { api } from '@/api/client'
import { AppLayout } from '@/components/layout/AppLayout'
import { ErrorScreen } from '@/components/shared/ErrorScreen'
import { WorkspaceProvider } from '@/contexts/WorkspaceContext'
import { useRealtimeEvents } from '@/hooks/useRealtimeEvents'
import { useAuthStore } from '@/stores/auth.store'
import { useQuery } from '@tanstack/react-query'
import { Outlet, createRoute, redirect } from '@tanstack/react-router'
import { useEffect } from 'react'
import { Route as rootRoute } from '../__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app-layout',
  errorComponent: ({ error, reset }) => <ErrorScreen error={error} reset={reset} />,
  beforeLoad: ({ location }) => {
    const { isAuthenticated, user } = useAuthStore.getState()
    if (!isAuthenticated) {
      throw redirect({ to: '/auth/login', search: { redirect: location.href } })
    }
    // A temp password (admin-created account or admin reset) forces a
    // rotation before any app route is reachable — every navigation
    // into app-layout re-runs this check, not just the post-login one.
    if (user?.must_change_password) {
      throw redirect({ to: '/change-password' })
    }
  },
  component: AppLayoutWrapper,
})

function AppLayoutWrapper() {
  useRealtimeEvents()

  const setAuth = useAuthStore((s) => s.setAuth)
  const currentUser = useAuthStore((s) => s.user)
  const currentOrg = useAuthStore((s) => s.org)
  const currentPerms = useAuthStore((s) => s.permissions)

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.auth.me(),
    staleTime: 5 * 60_000,
    // PERF audit Lot 3.5 (2026-07-09): auth-critical query — pause instead
    // of firing a doomed fetch while genuinely offline (the global default
    // in lib/query.ts is 'offlineFirst', tuned for cached list views).
    networkMode: 'online',
  })

  useEffect(() => {
    if (!meQuery.data) return
    const { user, org, permissions } = meQuery.data
    const fresh =
      user.id !== currentUser?.id ||
      org.id !== currentOrg?.id ||
      permissions.length !== currentPerms.length ||
      permissions.some((p, i) => p !== currentPerms[i])
    if (fresh) {
      setAuth(user, org, permissions, {
        access_token: useAuthStore.getState().accessToken ?? '',
        // SEC-1: refresh token lives in the HttpOnly cookie; no JS access.
        refresh_token: '',
        expires_in: 0,
        token_type: 'Bearer',
      })
    }
  }, [meQuery.data, currentUser, currentOrg, currentPerms, setAuth])

  return (
    <WorkspaceProvider>
      <AppLayout>
        <Outlet />
      </AppLayout>
    </WorkspaceProvider>
  )
}
